import { useEffect, useRef, useState } from "react";
import { X, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import SlideFilteredImage from "@/components/SlideFilteredImage";

// Free, client-side preview of a Minute Mode reel: plays each slide (image + on-screen text)
// for its duration inside a 9:16 portrait frame — no server render. Optionally plays the chosen
// song (looped) + the recorded voice-over so timing can be judged with sound.
export default function MinutePreviewModal({ slides = [], durations = [], hideCounter = false, trackId = null, voUrl = null, endcard = null, onClose }) {
  const slideItems = slides.map((s, i) => ({ ...s, dur: durations[i] || 5, n: i + 1 }));
  const showEndcard = !!(endcard && endcard.enabled);
  const items = showEndcard ? [...slideItems, { isEndcard: true, dur: 4 }] : slideItems;
  const slideCount = slideItems.length;
  const total = items.reduce((a, b) => a + b.dur, 0);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const startRef = useRef(Date.now());
  const rafRef = useRef(null);
  const musicRef = useRef(null);
  const voRef = useRef(null);
  const musicObjUrl = useRef(null);
  const hasSound = !!(trackId || voUrl);

  const src = (s) => s.image || (s.image_id ? imageUrl(s.image_id) : null);

  // Load the chosen song (with auth, as a blob) + set up the voice-over element.
  useEffect(() => {
    let off = false;
    (async () => {
      if (trackId) {
        try {
          const res = await api.get(`/me/music/${trackId}/audio`, { responseType: "blob" });
          if (off) return;
          musicObjUrl.current = URL.createObjectURL(res.data);
          const a = new Audio(musicObjUrl.current);
          a.loop = true;
          musicRef.current = a;
          if (playing && !muted) a.play().catch(() => {});
        } catch { /* no music */ }
      }
    })();
    if (voUrl) { const v = new Audio(voUrl); voRef.current = v; if (playing && !muted) v.play().catch(() => {}); }
    return () => {
      off = true;
      [musicRef, voRef].forEach((r) => { if (r.current) { try { r.current.pause(); } catch { /* noop */ } r.current = null; } });
      if (musicObjUrl.current) URL.revokeObjectURL(musicObjUrl.current);
    };
  }, []); // eslint-disable-line

  const setAudioMuted = (m) => { [musicRef, voRef].forEach((r) => { if (r.current) r.current.muted = m; }); };
  const restartAudio = () => {
    [musicRef, voRef].forEach((r) => { if (r.current) { try { r.current.currentTime = 0; if (!muted) r.current.play().catch(() => {}); } catch { /* noop */ } } });
  };
  const pauseAudio = () => { [musicRef, voRef].forEach((r) => { if (r.current) { try { r.current.pause(); } catch { /* noop */ } } }); };

  useEffect(() => {
    if (!playing) { pauseAudio(); return; }
    setAudioMuted(muted);
    [musicRef, voRef].forEach((r) => { if (r.current && !muted) r.current.play().catch(() => {}); });
    startRef.current = Date.now() - elapsed * 1000;
    const tick = () => {
      const e = (Date.now() - startRef.current) / 1000;
      if (e >= total) { setElapsed(total); setIdx(items.length - 1); setPlaying(false); return; }
      setElapsed(e);
      let acc = 0, cur = 0;
      for (let i = 0; i < items.length; i++) { acc += items[i].dur; if (e < acc) { cur = i; break; } }
      setIdx(cur);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]); // eslint-disable-line

  const toggleMute = () => { setMuted((m) => { const n = !m; setAudioMuted(n); if (!n && playing) [musicRef, voRef].forEach((r) => r.current && r.current.play().catch(() => {})); return n; }); };
  const replay = () => { setElapsed(0); setIdx(0); startRef.current = Date.now(); restartAudio(); setPlaying(true); };
  const cur = items[idx] || items[0];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={onClose} data-testid="minute-preview-overlay">
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center">
        <div className="relative rounded-[26px] overflow-hidden bg-black border-2 border-[#5d8a52]" style={{ width: "min(88vw, 300px)", aspectRatio: "9 / 16", boxShadow: "0 30px 90px -24px rgba(0,0,0,.85)" }} data-testid="minute-preview-stage">
          {cur?.isEndcard ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" style={{ background: endcard.bg_color || "#1a2818", color: endcard.text_color || "#ffffff" }} data-testid="minute-preview-endcard">
              {(endcard.logo_image || endcard.logo_image_id) && (
                <img src={endcard.logo_image || imageUrl(endcard.logo_image_id)} alt="logo" className="max-h-16 max-w-[55%] object-contain mb-3" />
              )}
              {[endcard.line1, endcard.line2, endcard.line3].filter((l) => (l || "").trim()).map((l, i) => (
                <div key={i} className="font-extrabold leading-tight break-words" style={{ fontSize: ({ small: 16, medium: 22, large: 28 }[endcard.font_size || "medium"]), textShadow: "0 2px 8px rgba(0,0,0,.55)" }}>{l}</div>
              ))}
              {(endcard.domain || "").trim() && <div className="mt-2 text-[12px] font-bold opacity-90 break-all" style={{ textShadow: "0 2px 8px rgba(0,0,0,.55)" }}>{endcard.domain}</div>}
              {(endcard.qr_image || endcard.qr_image_id) && (
                <img src={endcard.qr_image || imageUrl(endcard.qr_image_id)} alt="qr" className="mt-3 w-16 h-16 object-contain bg-white rounded-md p-0.5" />
              )}
              {endcard.show_branding && (
                <div className="absolute bottom-3 inset-x-0 text-[10px] font-extrabold opacity-85">Step<span style={{ color: "#a9c39c" }}>My</span>Step</div>
              )}
            </div>
          ) : (
            <>
              {cur && src(cur) ? (
                <SlideFilteredImage src={src(cur)} filter={cur.filter || "none"} strength={cur.filter_strength || "standard"} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-[#0f0f16]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

              {!hideCounter && (
                <span className="absolute top-3 left-3 text-[13px] font-extrabold text-black bg-[#ffd21e] px-2 py-0.5 rounded-full">{cur?.n} of {slideCount}</span>
              )}

              {(() => {
                const pos = cur?.text_pos || "middle";
                const sz = cur?.text_size || "medium";
                const hFont = { small: "clamp(16px,5.5vw,24px)", medium: "clamp(20px,7vw,30px)", large: "clamp(24px,8.5vw,38px)" }[sz];
                const sFont = { small: "clamp(11px,3vw,13px)", medium: "clamp(12px,3.6vw,15px)", large: "clamp(14px,4.2vw,18px)" }[sz];
                const wrap = pos === "top"
                  ? "absolute inset-x-0 top-0 pt-[13%] px-4 text-center"
                  : pos === "bottom"
                  ? "absolute inset-x-0 bottom-0 pb-[15%] px-4 text-center"
                  : "absolute inset-0 flex flex-col items-center justify-center px-4 text-center";
                return (
                  <div className={wrap}>
                    {cur?.headline && <div className="text-white font-extrabold leading-tight" style={{ fontSize: hFont, textShadow: "0 2px 10px rgba(0,0,0,.9)" }}>{cur.headline}</div>}
                    {cur?.subtext && <div className="text-[#ffd21e] font-semibold mt-1.5" style={{ fontSize: sFont, textShadow: "0 2px 8px rgba(0,0,0,.9)" }}>{cur.subtext}</div>}
                  </div>
                );
              })()}
            </>
          )}

          {/* per-slide progress segments */}
          <div className="absolute top-2.5 inset-x-3 flex gap-1">
            {items.map((s, i) => {
              const before = items.slice(0, i).reduce((a, b) => a + b.dur, 0);
              const p = Math.max(0, Math.min(1, (elapsed - before) / s.dur));
              return <div key={i} className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white" style={{ width: `${p * 100}%` }} /></div>;
            })}
          </div>

          {!playing && (
            <button onClick={replay} data-testid="minute-preview-replay" className="absolute inset-0 flex items-center justify-center bg-black/40">
              <span className="inline-flex items-center gap-2 rounded-full bg-white text-black font-extrabold px-5 py-2.5"><RotateCcw size={18} /> Replay</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 mt-4">
          {playing ? (
            <span className="text-white/70 text-sm font-bold tabular-nums" data-testid="minute-preview-time">{elapsed.toFixed(0)}s / {total}s</span>
          ) : (
            <button onClick={replay} className="inline-flex items-center gap-1.5 text-sm font-extrabold text-white/80 hover:text-white"><Play size={16} /> Play again</button>
          )}
          {hasSound && (
            <button onClick={toggleMute} data-testid="minute-preview-mute" title={muted ? "Unmute" : "Mute"} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold px-3 py-2 text-sm transition-colors">
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          )}
          <button onClick={onClose} data-testid="minute-preview-close" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 text-sm transition-colors"><X size={16} /> Close</button>
        </div>
        <p className="text-white/40 text-[11px] mt-2">{hasSound ? "Preview with your song & voice-over · final render mixes them cleanly" : "Silent visual preview · add music & voice above to hear them here"}</p>
      </div>
    </div>
  );
}
